import { getInvoiceRuleGetProcessingReport, getInvoiceRuleGetSystemProcessingReport } from '@apis/Invoices';
import { AllocationDimension, InvoiceAllocationRule, InvoiceProcessingReport, InvoiceTaggingRule } from '@apis/Invoices/model';
import { inject, injectable } from 'tsyringe';
import { FormatService } from '../FormatService';
import { Logger } from '../Logger';
import { AdjPivotName } from './InvoiceSchemaService';
import { Platform } from '../PlatformService';

export type InvoiceAdjustmentInfo = {
    fieldName: AdjPivotName;
} & ({ type: 'Tag'; rule: InvoiceTaggingRule } | { type: Exclude<AdjPivotName['type'], 'Tag'>; rule: InvoiceAllocationRule });

export interface AdjustmentRulesResult {
    rules: InvoiceAdjustmentInfo[] | null;
    platform: Platform;
}

@injectable()
export class InvoiceAdjustmentService {
    public constructor(@inject(FormatService) private readonly fmtSvc: FormatService, @inject(Logger) private readonly logger: Logger) {}

    public async getAdjustmentRules(month: Date): Promise<AdjustmentRulesResult> {
        const processingReport = await this.getProcessingReport(month);
        const [allocDim, platform] = this.deserAllocDim(processingReport);

        return { rules: !allocDim ? null : this.getRuleAdjustmentFields(allocDim), platform: platform ?? 'Aws' };
    }

    public async getSystemAdjustmentRules(month: Date, companyId: number): Promise<AdjustmentRulesResult> {
        const processingReport = await this.getSystemProcessingReport(month, companyId);
        const [allocDim, platform] = this.deserAllocDim(processingReport);

        return { rules: !allocDim ? null : this.getRuleAdjustmentFields(allocDim), platform: platform ?? 'Aws' };
    }

    private getRuleAdjustmentFields(allocDim: AllocationDimension | null) {
        type RuleItem = { Order: number | null | undefined; Status: 'Active' | 'Draft' | 'Test' };
        const getNames = (type: AdjPivotName['type'], items: null | undefined | RuleItem[]) =>
            items
                ?.filter((o) => typeof o.Order === 'number' && o.Status === 'Active')
                .sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0))
                .map(
                    (rule) =>
                        ({
                            fieldName: new AdjPivotName(type, rule.Order!),
                            type,
                            rule,
                        } as InvoiceAdjustmentInfo)
                ) ?? [];

        return [
            ...getNames('Sys', allocDim?.SystemAllocationRules),
            ...getNames('Tag', allocDim?.TaggingRules),
            ...getNames('Uca', allocDim?.AllocationRules),
            ...getNames(
                'Uam',
                allocDim?.AllocationRules?.filter((r) => r.FundSource?.Sources?.some((s) => s.Amortization?.AmountExpr))
            ),
        ];
    }

    private deserAllocDim(report: InvoiceProcessingReport): [AllocationDimension | null, Platform] {
        const lastSuccess = report.LastSuccess?.IndexingJobs?.find((j) => j.AllocDimSnapshot);
        try {
            const platform = lastSuccess?.Platform as Platform;
            const rawAllocDim = lastSuccess?.AllocDimSnapshot;
            const allocDim = !rawAllocDim ? null : (JSON.parse(rawAllocDim) as AllocationDimension);
            return [allocDim, platform];
        } catch (error) {
            this.logger.warn(`Failed to parse allocation dimension snapshot from InvoiceProcessingLogEntry: ${lastSuccess?.Id}`, { error });
            return [null, 'Aws'];
        }
    }

    private getProcessingReport(month: Date) {
        return getInvoiceRuleGetProcessingReport({ month: this.fmtSvc.toJsonShortDate(month) });
    }

    private getSystemProcessingReport(month: Date, companyId: number) {
        return getInvoiceRuleGetSystemProcessingReport({ month: this.fmtSvc.toJsonShortDate(month), companyId: companyId });
    }
}
